/**
 * ImageUploadField.tsx
 *
 * Image upload field component with custom API endpoint support.
 * - Displays UI for selecting an image from the device (no camera)
 * - Validates that customApi is configured (required for form submission)
 * - Stores File objects temporarily in form state
 * - Uploads are handled during form submission via the FormRenderer
 */

import React, { useRef, useState, useEffect } from 'react';
import { Upload, X, AlertCircle } from 'lucide-react';
import { BaseFieldProps } from './shared/types';
import { FieldWrapper } from './shared';
import { Button } from '../ui/button';

export const ImageUploadField: React.FC<BaseFieldProps> = React.memo(({
  fieldConfig,
  mode,
  value,
  onChange,
  errors = [],
  disabled = false,
  hasError = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const hasCustomApi = fieldConfig.customApi?.enabled && fieldConfig.customApi?.url;

  if (!hasCustomApi && mode !== 'preview') {
    const errorMessage = `Custom API endpoint not configured for image upload. Please configure the upload endpoint in the form builder.`;

    return (
      <FieldWrapper
        field={fieldConfig}
        errors={[...errors, errorMessage]}
        hasError={true}
      >
        <div className="border-2 border-red-300 rounded-md p-6 bg-red-50 flex gap-3">
          <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-red-700 font-medium">Image Upload Not Configured</p>
            <p className="text-xs text-red-600 mt-1">{errorMessage}</p>
          </div>
        </div>
      </FieldWrapper>
    );
  }

  if (!hasCustomApi && mode === 'preview') {
    return (
      <FieldWrapper
        field={fieldConfig}
        errors={errors}
        hasError={hasError}
        isPreview={true}
      >
        <div className="border-2 border-yellow-300 rounded-md p-6 bg-yellow-50 flex gap-3">
          <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-yellow-700 font-medium">Image Upload Endpoint Not Configured</p>
            <p className="text-xs text-yellow-600 mt-1">
              Configure a custom API endpoint in the field settings before publishing
            </p>
          </div>
        </div>
      </FieldWrapper>
    );
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onChange(fieldConfig.id, file);
    }
    // Reset the input so selecting the same file again triggers onChange
    e.target.value = '';
  };

  const handleRemove = () => {
    onChange(fieldConfig.id, null);
  };

  const isImage = value instanceof File;
  const isImageUrl = typeof value === 'string' && value.length > 0;

  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [imageLoadError, setImageLoadError] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);

  useEffect(() => {
    if (value instanceof File) {
      const url = URL.createObjectURL(value);
      setBlobUrl(url);
      setImageLoadError(false);
      setImageLoading(false);
      return () => URL.revokeObjectURL(url);
    }
    setBlobUrl(null);
    setImageLoadError(false); // Reset error state when value changes
    // If it's a URL string, mark as loading until image loads
    if (typeof value === 'string' && value.length > 0) {
      setImageLoading(true);
    }
  }, [value]);

  const isPreviewMode = mode === 'preview';

  return (
    <FieldWrapper
      field={fieldConfig}
      errors={errors}
      hasError={hasError}
      isPreview={isPreviewMode}
    >
      <div className="space-y-3">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          disabled={disabled}
          onChange={handleFileChange}
          className="sr-only"
        />
        {(isImage || isImageUrl) ? (
          <div className="space-y-3">
            {isImageUrl && imageLoadError ? (
              <div className="border-2 border-yellow-300 rounded-lg p-6 bg-yellow-50 flex gap-3">
                <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-yellow-700 font-medium">Previously submitted image exists, but unable to retrieve it</p>
                  <p className="text-xs text-yellow-600 mt-1">The image may have been deleted or is no longer accessible.</p>
                </div>
              </div>
            ) : (
              <>
                <img
                  src={isImageUrl ? value as string : blobUrl || undefined}
                  alt="preview"
                  className={`w-full max-w-md h-auto object-cover rounded-lg border border-gray-200 ${imageLoading && isImageUrl ? 'opacity-50' : ''}`}
                  onError={() => {
                    if (isImageUrl) {
                      setImageLoadError(true);
                      setImageLoading(false);
                    }
                  }}
                  onLoad={() => {
                    if (isImageUrl) {
                      setImageLoadError(false);
                      setImageLoading(false);
                    }
                  }}
                />
                {imageLoading && isImageUrl && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                )}
              </>
            )}
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={disabled}
                className="gap-2"
              >
                <Upload className="h-4 w-4" />
                Change Image
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleRemove}
                disabled={disabled}
                className="gap-2"
              >
                <X className="h-4 w-4" />
                Remove
              </Button>
            </div>
            {isImage && (
              <p className="text-xs text-gray-500">
                Image will be uploaded when you submit the form
              </p>
            )}
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled}
              className="gap-2"
            >
              <Upload className="h-4 w-4" />
              Upload Image
            </Button>
          </div>
        )}
      </div>
    </FieldWrapper>
  );
});

ImageUploadField.displayName = 'ImageUploadField';
